/**
 * Tiny per-page SEO helper.
 *
 * Updates <title>, <meta description>, <link canonical> and an optional
 * <script type="application/ld+json"> block at runtime, then restores the
 * previous values on cleanup so single-page navigation doesn't leak.
 *
 * No external deps (avoids the react-helmet bundle).
 *
 * Usage:
 *   useSeo({
 *     title: "Camping near Glastonbury — CampCompass UK",
 *     description: "12 verified pitches within 25 miles of Glastonbury Festival …",
 *     canonical: "/festivals/glastonbury-2026",
 *     jsonLd: { "@context": "https://schema.org", "@type": "Event", … },
 *   });
 */
import { useEffect } from "react";

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

function setMeta(name, content, useProperty = false) {
  if (!content) return null;
  const attr = useProperty ? "property" : "name";
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  let created = false;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
    created = true;
  }
  const prev = tag.getAttribute("content");
  tag.setAttribute("content", content);
  return { tag, prev, created };
}

function setCanonical(href) {
  if (!href) return null;
  let tag = document.head.querySelector('link[rel="canonical"]');
  let created = false;
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
    created = true;
  }
  const prev = tag.getAttribute("href");
  tag.setAttribute("href", href);
  return { tag, prev, created };
}

function setJsonLd(id, data) {
  if (!data) return null;
  let tag = document.head.querySelector(`script[data-seo-id="${id}"]`);
  let created = false;
  if (!tag) {
    tag = document.createElement("script");
    tag.setAttribute("type", "application/ld+json");
    tag.setAttribute("data-seo-id", id);
    document.head.appendChild(tag);
    created = true;
  }
  const prev = tag.textContent;
  tag.textContent = JSON.stringify(data);
  return { tag, prev, created };
}

export function useSeo({ title, description, canonical, ogImage, jsonLd, jsonLdId = "page", noindex = false }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const descMeta = setMeta("description", description);
    const ogTitle = setMeta("og:title", title, true);
    const ogDesc = setMeta("og:description", description, true);
    const ogImg = setMeta("og:image", ogImage, true);
    const twTitle = setMeta("twitter:title", title);
    const twDesc = setMeta("twitter:description", description);
    const twImg = setMeta("twitter:image", ogImage);
    // iter222 — opt-in `noindex` for utility pages (login, register,
    // auth callback). Lets Google *crawl* them (so the "Indexed, though
    // blocked by robots.txt" warning clears) while still keeping them
    // out of the index.
    const robotsMeta = noindex ? setMeta("robots", "noindex, follow") : null;

    const canonicalUrl = canonical
      ? (canonical.startsWith("http") ? canonical : `${PUBLIC_URL}${canonical}`)
      : null;
    const canonicalRef = setCanonical(canonicalUrl);
    const ogUrl = setMeta("og:url", canonicalUrl, true);

    const ldRef = setJsonLd(jsonLdId, jsonLd);

    return () => {
      // Restore title; meta tags revert to previous content if we changed
      // them, or get removed if we created them fresh.
      document.title = prevTitle;
      [descMeta, ogTitle, ogDesc, ogImg, twTitle, twDesc, twImg, ogUrl, robotsMeta].forEach((ref) => {
        if (!ref) return;
        if (ref.created) ref.tag.remove();
        else if (ref.prev !== null) ref.tag.setAttribute("content", ref.prev);
      });
      if (canonicalRef) {
        if (canonicalRef.created) canonicalRef.tag.remove();
        else if (canonicalRef.prev) canonicalRef.tag.setAttribute("href", canonicalRef.prev);
      }
      if (ldRef) {
        if (ldRef.created) ldRef.tag.remove();
        else ldRef.tag.textContent = ldRef.prev || "";
      }
    };
  // We want this to fire whenever any input changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, ogImage, noindex, JSON.stringify(jsonLd || null)]);
}

export default useSeo;
